import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './residents.css';
import { sx } from '../../ui/designStyles';
import { useAtlasSubject, usePageTab } from '../../shell/Shell';
import { useResidentsCtx, type ResidentsCtx } from './common';
import { pre } from './dom';
import { teamAccessActive } from '@aithora/shared';
import { useConsoleRole } from '../../session/SessionContext';
import { useFacilityScope } from '../../session/FacilityScope';
import { BreakGlassRequestCard, TeamAccessBanner, TeamAccessRequest } from './TeamAccess';
import { RosterTab } from './RosterTab';
import { PlansTab } from './PlansTab';
import { AlertsTab } from './AlertsTab';
import { ConsentTab } from './ConsentTab';
import { NotesTab } from './NotesTab';

// A port of the Console V3 design's Residents page (T1992-3911, logic L38361-39470 and
// the helpers it calls). The markup is the design's own, converted element for element;
// every figure comes from the back end (see packages/shared residents-routes.ts).

export type ResidentsTabKey = 'roster' | 'plans' | 'alerts' | 'consent' | 'notes';

const TAB_LABEL: Record<ResidentsTabKey, string> = { roster: 'Residents', plans: 'Care Plans', alerts: 'Alerts', consent: 'Consent', notes: 'Clinical Notes' };

/** The page tabs (design rTabs, L38372): Clinical Notes for the Facility Admin and Aithora only. */
export function residentTabs(ctx: ResidentsCtx): ResidentsTabKey[] {
  const base: ResidentsTabKey[] = ['roster', 'plans', 'alerts', 'consent'];
  return ctx.persona === 'Facility Admin' || ctx.persona === 'Operator' ? base.concat(['notes']) : base;
}

export function Residents() {
  const ctx = useResidentsCtx();
  const { role } = useConsoleRole();
  const [raw, setTab] = useState<ResidentsTabKey>('roster');
  // Deep link from another page ("Chart ->"): ?resident=<id> opens that chart.
  const [focus, setFocus] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('resident');
  });
  // The same link while Residents is already open (an Atlas answer's link, answer-links.ts).
  const linked = new URLSearchParams(useLocation().search).get('resident');
  useEffect(() => {
    if (!linked) return;
    setFocus(linked);
    setTab('roster');
  }, [linked]);
  const tabs = ctx ? residentTabs(ctx) : [];
  const t: ResidentsTabKey = tabs.includes(raw) ? raw : 'roster';
  usePageTab(ctx ? TAB_LABEL[t] : null);
  // The selected resident rides on the top bar's Atlas button on every tab (design atlasSubject).
  const [subject, setSubject] = useState<string | null>(null);
  useAtlasSubject(subject);
  if (!ctx) return null;
  const page = <ResidentsPage ctx={ctx} tab={t} tabs={tabs} setTab={setTab} focus={focus} setFocus={setFocus} setSubject={setSubject} />;
  return role === 'OPERATOR' ? <OperatorGate ctx={ctx}>{page}</OperatorGate> : page;
}

/**
 * V7 (bgLayer restrict): the Operator sees no resident records by default, only
 * "Restricted: request access" for the building in view, until that building grants access
 * for a set time. The server keeps its own rule (team-access.ts); this is the stricter look
 * the design draws. Super Admin and Partner keep the page as the server answers it.
 */
function OperatorGate({ ctx, children }: { ctx: ResidentsCtx; children: ReactNode }) {
  const { inView, directory } = useFacilityScope();
  const bldg = inView[0] ?? null;
  const dirWaiting = !directory.data && !directory.error;
  const [grant, setGrant] = useState<'loading' | 'none' | 'active'>('loading');
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!bldg) { setGrant(dirWaiting ? 'loading' : 'none'); return; }
    let live = true;
    setGrant('loading');
    teamAccessActive(ctx.client, { query: { organizationId: bldg.id } })
      .then((r) => { if (live) setGrant(r.active && Date.parse(r.active.endsAt) > Date.now() ? 'active' : 'none'); })
      .catch(() => { if (live) setGrant('none'); });
    return () => { live = false; };
  }, [ctx.client, bldg?.id, dirWaiting, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  if (grant === 'loading') return null;
  if (grant === 'active') {
    return (
      <>
        <TeamAccessBanner client={ctx.client} orgId={bldg?.id ?? null} residentId={null} buildingName={bldg?.name ?? null} onEnded={() => setTick((t) => t + 1)} />
        {children}
      </>
    );
  }
  return (
    <div data-screen-label="Residents" data-testid="residents" style={sx('display:flex;flex-direction:column;gap:11px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow-y:auto;')}>
      <TeamAccessRequest client={ctx.client} orgId={bldg?.id ?? null} residentId={null} buildingName={bldg?.name ?? null} onSent={() => setTick((t) => t + 1)} />
    </div>
  );
}

function ResidentsPage({
  ctx, tab, tabs, setTab, focus, setFocus, setSubject,
}: {
  ctx: ResidentsCtx;
  tab: ResidentsTabKey;
  tabs: ResidentsTabKey[];
  setTab: (t: ResidentsTabKey) => void;
  focus: string | null;
  setFocus: (id: string | null) => void;
  setSubject: (name: string | null) => void;
}) {
  const navigate = useNavigate();
  const openChart = (residentId: string) => {
    setFocus(residentId);
    setTab('roster');
  };
  return (
    <div data-screen-label="Residents" data-testid="residents" style={sx('display:flex;flex-direction:column;gap:11px;min-width:0;flex:1 1 auto;min-height:0;height:100%;overflow:hidden;')}>
      {' '}
      {/* V6 "Break-glass request": Aithora asking this building to see resident records. */}
      {ctx.persona === 'Facility Admin' && <BreakGlassRequestCard client={ctx.client} onSeeLog={() => navigate('/settings?section=Break-glass')} />}
      <div role="tablist" aria-label="Residents sections" style={sx('flex:0 0 auto;display:flex;gap:6px;min-width:0;')}>
        {' '}
        {tabs.map((k) => {
          const on = tab === k;
          return (
            <Fragment key={k}>
              {' '}
              <a
                href="#"
                role="tab"
                aria-selected={on}
                onClick={(e) => { pre(e); setTab(k); }}
                style={sx('display:inline-block;padding:7px 14px;border-radius:7px;font-size:16px;text-decoration:none;' + (on ? 'background:rgba(79,138,214,.12);color:#2F5C97;font-weight:600;' : 'color:#54606C;'))}
              >
                <span>{TAB_LABEL[k]}</span>
              </a>{' '}
            </Fragment>
          );
        })}{' '}
      </div>{' '}
      <div style={sx('flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;display:flex;flex-direction:column;gap:11px;padding-right:3px;')}>
        {' '}
        {tab === 'roster' && <RosterTab ctx={ctx} focus={focus} setFocus={setFocus} setSubject={setSubject} />}
        {tab === 'plans' && <PlansTab ctx={ctx} openChart={openChart} />}
        {tab === 'alerts' && <AlertsTab ctx={ctx} />}
        {tab === 'consent' && <ConsentTab ctx={ctx} />}
        {tab === 'notes' && <NotesTab ctx={ctx} />}
      </div>{' '}
    </div>
  );
}
